import type { LoadState } from './ScreenStates';

export interface LoadableQuery {
  readonly data: unknown;
  readonly isError: boolean;
  readonly isFetching: boolean;
  readonly fetchStatus: 'fetching' | 'paused' | 'idle';
  readonly error: unknown;
  readonly errorUpdatedAt: number;
  readonly dataUpdatedAt: number;
  readonly failureCount: number;
  readonly failureReason: unknown;
  readonly refetch: () => unknown;
}

export const loadStateOf = (query: LoadableQuery): LoadState => {
  const paused = query.fetchStatus === 'paused';
  const retryWaiting = paused && query.failureCount > 0;
  // Retrying only when the last answer was a failure: a screen that selects part of a newer answer is merely loading.
  const retrying = query.data === undefined && query.isFetching && query.errorUpdatedAt > query.dataUpdatedAt;
  const hasData = query.data !== undefined;
  return {
    hasData,
    isError: query.isError || retryWaiting || retrying,
    isPaused: paused && query.failureCount === 0,
    isRetrying: retrying,
    isStale: hasData && (paused || query.isError),
    loadError: query.error ?? query.failureReason,
    refetch: () => {
      void query.refetch();
    },
  };
};

export const combineLoadStates = (...states: readonly LoadState[]): LoadState => {
  const blocking = states.filter((state) => !state.hasData);
  return {
    hasData: blocking.length === 0,
    isError: blocking.some((state) => state.isError),
    isPaused: blocking.some((state) => state.isPaused),
    isRetrying: blocking.some((state) => state.isRetrying),
    isStale: states.some((state) => state.isStale === true),
    loadError: blocking.find((state) => state.isError)?.loadError ?? null,
    refetch: () => states.forEach((state) => state.refetch()),
  };
};
